import { logger } from 'common/lib/logger'
import { useState } from 'react'
import { FileType, useUploadFileMutation } from '../../__generated__/schema'

export const determineMimeType = (extension: string | undefined) => {
    if (!extension) {
        logger.error('No file extension provided')
        return null
    }

    switch (extension) {
        case 'jpg':
        case 'jpeg':
            return 'image/jpeg'
        case 'png':
            return 'image/png'
        case 'csv':
            return 'text/csv'
        case 'mp4':
            return 'video/mp4'
        case 'json':
        case 'mup':
            return 'application/json'
        default:
            logger.error('Unsupported file type', { extension })
            return null
    }
}
export default function createLogin() {
    return {
        useMindMapEditor: () => {
            const [pending, setPending] = useState(false)
            const [getUploadFileUrl] = useUploadFileMutation()
            return {
                mutate: async (imageUrl: string, previewUrl: string) => {
                    setPending(true)
                    try {
                        const resultImage = await getUploadFileUrl({ variables: { type: FileType.Image } })
                        const resultPreview = await getUploadFileUrl({ variables: { type: FileType.Image } })
                        if (!resultImage.data?.uploadFile?.success || !resultPreview.data?.uploadFile?.success) {
                            setPending(false)
                            return false
                        }

                        await fetch(resultImage.data.uploadFile.uploadUrl!, {
                            method: 'PUT',
                            body: await fetch(imageUrl).then((res) => res.blob()),
                        })

                        await fetch(resultPreview.data.uploadFile.uploadUrl!, {
                            method: 'PUT',
                            body: await fetch(previewUrl).then((res) => res.blob()),
                        })
                        return {
                            imageId: resultImage.data.uploadFile.objectId!,
                            previewId: resultPreview.data.uploadFile.objectId!,
                        }
                    } catch (err) {
                        logger.error(err)
                        setPending(false)

                        return false
                    }
                },
                loading: pending,
            }
        },
        useUploadFile: (
            type: FileType
        ): [(fileUrl: string, filename: string) => Promise<false | [string, string]>, boolean] => {
            const [pending, setPending] = useState(false)
            const [getUploadFileUrl] = useUploadFileMutation()
            return [
                async (fileUrl, filename) => {
                    setPending(true)

                    const extension = filename.split('.').pop()
                    const mimeType = determineMimeType(extension)
                    if (!mimeType) {
                        setPending(false)
                        return false
                    }

                    try {
                        const result = await getUploadFileUrl({ variables: { type } })
                        if (!result.data?.uploadFile?.success) {
                            logger.error('Upload file URL mutation failed', { result })
                            setPending(false)
                            return false
                        }

                        let fileBlob
                        if (fileUrl.startsWith('blob:')) {
                            const response = await fetch(fileUrl)
                            if (!response.ok) {
                                logger.error('Failed to fetch file', { fileUrl, status: response.status })
                                setPending(false)
                                return false
                            }
                            fileBlob = await response.blob()
                        } else {
                            fileBlob = new Blob([fileUrl], { type: mimeType })
                        }

                        const uploadResponse = await fetch(result.data.uploadFile.uploadUrl!, {
                            headers: new Headers({
                                'X-Amz-Meta-filename': filename,
                            }),
                            method: 'PUT',
                            body: fileBlob,
                        })

                        if (!uploadResponse.ok) {
                            logger.error('File upload failed', { status: uploadResponse.status, filename })
                            setPending(false)
                            return false
                        }

                        setPending(false)
                        return [
                            result.data.uploadFile.objectId! as string,
                            result.data.uploadFile.objectUrl! as string,
                        ]
                    } catch (err) {
                        logger.error(err)

                        setPending(false)
                        return false
                    }
                },
                pending,
            ]
        },
    }
}
